import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { DashboardStats } from '../types';
import { fetchDashboardStats } from '../api/dashboard';
import StatusBadge from '../components/StatusBadge';

// -------------------------------------------------------------------------
// Stat card component — reused within dashboard only
// -------------------------------------------------------------------------

interface StatCardProps {
  label: string;
  value: number | string;
  description?: string;
  href?: string;
  colorClass?: string;
}

function StatCard({ label, value, description, href, colorClass = 'text-brand-700' }: StatCardProps) {
  const inner = (
    <>
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className={['mt-1 text-3xl font-bold', colorClass].join(' ')}>{value}</p>
      {description && <p className="mt-1 text-xs text-gray-400">{description}</p>}
    </>
  );

  if (href) {
    return (
      <Link
        to={href}
        className="card block hover:shadow-md transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded-xl"
        aria-label={`${label}: ${value}`}
      >
        {inner}
      </Link>
    );
  }

  return <div className="card">{inner}</div>;
}

// -------------------------------------------------------------------------
// Mock data helper — replace with real API call when backend is ready
// -------------------------------------------------------------------------

function buildMockStats(): DashboardStats {
  return {
    total_tenders: 24,
    active_tenders: 7,
    tenders_won: 11,
    tenders_lost: 6,
    total_employees: 42,
    total_cvs: 38,
    pending_matches: 3,
    recent_tenders: [
      {
        id: 't1',
        title: 'Senior Cloud Architect — BUND Portal',
        client: 'Bundesministerium für Digitales',
        description: '',
        requirements: [],
        required_skills: ['AWS', 'Terraform', 'Kubernetes'],
        nice_to_have_skills: [],
        deadline: '2026-04-15',
        status: 'matching',
        created_at: '2026-03-10T09:00:00Z',
        updated_at: '2026-03-12T14:00:00Z',
      },
      {
        id: 't2',
        title: 'Data Engineer — Analytics Platform',
        client: 'Siemens AG',
        description: '',
        requirements: [],
        required_skills: ['Python', 'Apache Spark', 'dbt'],
        nice_to_have_skills: [],
        deadline: '2026-04-30',
        status: 'submitted',
        created_at: '2026-03-08T11:00:00Z',
        updated_at: '2026-03-08T11:00:00Z',
      },
      {
        id: 't3',
        title: 'Full-Stack Developer — E-Commerce',
        client: 'Otto GmbH & Co KG',
        description: '',
        requirements: [],
        required_skills: ['React', 'Node.js', 'PostgreSQL'],
        nice_to_have_skills: [],
        deadline: '2026-05-01',
        status: 'matched',
        created_at: '2026-03-01T08:00:00Z',
        updated_at: '2026-03-14T17:00:00Z',
      },
    ],
    recent_matches: [],
  };
}

// -------------------------------------------------------------------------
// Dashboard page
// -------------------------------------------------------------------------

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    fetchDashboardStats()
      .then((data) => {
        if (!cancelled) {
          setStats(data);
        }
      })
      .catch(() => {
        // Backend unavailable — fall back to mock data for local development.
        if (!cancelled) {
          setStats(buildMockStats());
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const dateLocale = i18n.resolvedLanguage === 'de' ? 'de-DE' : 'en-GB';

  if (loading) {
    return (
      <div role="status" aria-live="polite" className="flex items-center gap-3 text-gray-500">
        <span className="animate-spin text-brand-600 text-xl" aria-hidden="true">⟳</span>
        {t('dashboard.loadingDashboard')}
      </div>
    );
  }

  if (!stats) {
    return (
      <div role="alert" className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
        {t('dashboard.loadFailed')}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page heading */}
      <header>
        <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.title')}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {t('dashboard.subtitle')}
        </p>
      </header>

      {/* KPI stats grid */}
      <section aria-label={t('dashboard.kpiSection')}>
        <h2 className="sr-only">{t('dashboard.keyMetrics')}</h2>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4">
          <StatCard
            label={t('dashboard.totalTenders')}
            value={stats.total_tenders}
            href="/tenders"
          />
          <StatCard
            label={t('dashboard.activeTenders')}
            value={stats.active_tenders}
            href="/tenders"
            colorClass="text-amber-600"
          />
          <StatCard
            label={t('dashboard.tendersWon')}
            value={stats.tenders_won}
            colorClass="text-green-600"
          />
          <StatCard
            label={t('dashboard.pendingMatches')}
            value={stats.pending_matches}
            colorClass="text-orange-600"
            description={t('dashboard.awaitingAi')}
          />
          <StatCard
            label={t('dashboard.employees')}
            value={stats.total_employees}
            href="/cvs"
          />
          <StatCard
            label={t('dashboard.cvsUploaded')}
            value={stats.total_cvs}
            href="/cvs"
          />
          <StatCard
            label={t('dashboard.winRate')}
            value={`${Math.round((stats.tenders_won / (stats.tenders_won + stats.tenders_lost || 1)) * 100)}%`}
            colorClass="text-brand-700"
          />
          <StatCard
            label={t('dashboard.tendersLost')}
            value={stats.tenders_lost}
            colorClass="text-red-600"
          />
        </div>
      </section>

      {/* Recent tenders table */}
      <section aria-label={t('dashboard.recentTenders')} className="card overflow-hidden p-0">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">{t('dashboard.recentTenders')}</h2>
          <Link
            to="/tenders"
            className="text-sm text-brand-600 hover:text-brand-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
          >
            {t('dashboard.viewAll')}
          </Link>
        </div>

        <div className="overflow-x-auto">
          <table className="data-table" aria-label={t('dashboard.recentTendersList')}>
            <thead>
              <tr>
                <th scope="col">{t('dashboard.tender')}</th>
                <th scope="col">{t('dashboard.client')}</th>
                <th scope="col">{t('dashboard.deadline')}</th>
                <th scope="col">{t('common.status')}</th>
                <th scope="col">
                  <span className="sr-only">{t('common.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {stats.recent_tenders.map((tender) => (
                <tr key={tender.id}>
                  <td className="font-medium text-gray-900">{tender.title}</td>
                  <td>{tender.client}</td>
                  <td>
                    <time dateTime={tender.deadline}>
                      {new Date(tender.deadline).toLocaleDateString(dateLocale, {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </time>
                  </td>
                  <td>
                    <StatusBadge status={tender.status} />
                  </td>
                  <td>
                    {tender.status === 'matched' && (
                      <Link
                        to={`/tenders/${tender.id}/matches`}
                        className="text-xs text-brand-600 hover:text-brand-800 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-500 rounded"
                        aria-label={t('dashboard.viewMatchesFor', { title: tender.title })}
                      >
                        {t('dashboard.viewMatches')}
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Quick actions */}
      <section aria-label={t('dashboard.quickActions')} className="flex flex-wrap gap-3">
        <Link to="/tenders/submit" className="btn-primary">
          {t('dashboard.submitNewTender')}
        </Link>
        <Link to="/cvs" className="btn-secondary">
          {t('dashboard.uploadCv')}
        </Link>
        <Link to="/dpo" className="btn-secondary">
          {t('nav.dpoDashboard')}
        </Link>
      </section>
    </div>
  );
}
